/**
 * Pure derivations for the configuration pane's field list and for the properties
 * panel that opens as the editor's third column.
 */

import {
  FIELD_SELECTORS,
  IMAGE_FIELD_ID,
  PROPERTY_SECTIONS,
  getFieldDefinition,
  getRegionName,
} from '../constants';
import { CardCssRule } from '../styling';
import { FieldDefinition, FieldId, ProductCard, PropertySectionKind } from '../types';
import { selectPlacement } from './cardSelectors';

/** Every field, in the card's own pane order (spec R36, R41). */
export const selectRailFields = (card: ProductCard): FieldDefinition[] =>
  card.fieldOrder.map((fieldId) => getFieldDefinition(fieldId));

/** Pane rows left after the field search (spec R40, case-insensitive). */
export const selectVisibleFields = (
  card: ProductCard,
  searchTerm: string
): FieldDefinition[] => {
  const needle = searchTerm.trim().toLowerCase();
  const fields = selectRailFields(card);
  if (needle === '') {
    return fields;
  }
  return fields.filter((field) => field.name.toLowerCase().includes(needle));
};

/**
 * Re-exported from the vocabulary so the selector barrel keeps exporting the name
 * the panel and its tests already import (the union itself moved to `types/` when
 * `constants/PROPERTY_SECTIONS` became the data behind it).
 */
export type { PropertySectionKind } from '../types';

/**
 * Which sections a field's properties panel renders, in order (spec R66).
 *
 * The module's **one** decider: it reads `constants/PROPERTY_SECTIONS` and
 * `FieldConfigurationPanel` renders whatever comes back, so no component re-derives the
 * answer and adding a section for one field type cannot perturb another's
 * (ADR-4B). Before this round it derived from two `FieldDefinition` booleans, which
 * could not express Pricing's four ids, Button's one, or Description style's one.
 *
 * It does not decide whether the row shows an affordance — *every* enabled field
 * opens a panel (spec R46, amended).
 */
export const selectPropertySections = (fieldId: FieldId): PropertySectionKind[] => [
  // A copy: the table is module data, and a caller that mapped over it should not be
  // able to reorder every future panel by mutating what it was handed.
  ...PROPERTY_SECTIONS[fieldId],
];

/**
 * Whether a field has a Display label or an Alignment to configure.
 *
 * A derivation of `selectPropertySections`, so there is still one answer to "what
 * can this field configure". It describes the field's *sections*; it does not gate
 * the properties affordance (spec R46, amended) — every enabled field has a panel,
 * and since R66 every panel has at least Custom styling in it.
 */
export const selectHasConfiguration = (fieldId: FieldId): boolean =>
  selectPropertySections(fieldId).some(
    (section) => section === 'label' || section === 'alignment'
  );

/**
 * Every element's custom CSS contribution, in the card's own field order (R67).
 *
 * Raw text paired with the element's selector — the text is *not* interpreted here.
 * `styling/composeCardStylesheet` is the only thing that reads it, which is what
 * keeps R68's containment in one testable place (ADR-4A). Field order makes the
 * output stable, which matters for the two selectors the prototype has two fields
 * share (`.product-savings`, `.product-currency`): both rules are emitted, in a
 * defined order, rather than one silently winning.
 */
export const selectCardCssRules = (card: ProductCard): CardCssRule[] => {
  const rules: CardCssRule[] = [];
  card.fieldOrder.forEach((fieldId) => {
    const css = card.configuration[fieldId]?.customCss;
    if (css === undefined || css.trim() === '') {
      return;
    }
    rules.push({ selector: FIELD_SELECTORS[fieldId], css });
  });
  return rules;
};

/**
 * The region name a field's properties panel shows (spec R63), read from the one
 * region table the preview's own headers read (spec R48).
 *
 * The Primary Image has no placement — it *is* the photo background — so it
 * reports the photo region rather than nothing.
 */
export const selectPropertyRegionName = (card: ProductCard, fieldId: FieldId): string => {
  if (fieldId === IMAGE_FIELD_ID) {
    return getRegionName('photo');
  }
  const placement = selectPlacement(card, fieldId);
  return placement ? getRegionName(placement.region) : getRegionName('info');
};
